import { ChevronDown } from "lucide-react";
import { HERO_IMAGE, SAILOR } from "@/data/ships";

// The hero photo is always a dark stormy ocean, so we lock its overlay
// palette to "always light text on dark photo" regardless of theme.
const HERO_TEXT = "#F4F0E6";
const HERO_BRASS = "#D4AF37";
const HERO_BRASS_BRIGHT = "#E5C865";
const HERO_BG = "#080d14";

export default function Hero() {
  return (
    <section
      className="relative w-full h-screen-svh overflow-hidden flex items-end pb-14 sm:pb-20 md:pb-32 safe-pb"
      data-testid="hero-section"
      style={{ backgroundColor: HERO_BG, color: HERO_TEXT }}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Stormy ocean — Royal Navy tribute"
          className="w-full h-full object-cover drift"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(8,13,20,0.85), rgba(8,13,20,0.55), rgba(8,13,20,1))",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(8,13,20,0.7) 100%)",
          }}
        />
      </div>

      {/* Dad's own 1962 portrait — floats independently in the top-right
          of the hero so it never collides with the bottom-aligned headline
          or the fixed navigation bar. */}
      {SAILOR.portrait && (
        <figure
          className="polaroid hero-fade-soft absolute z-10 top-20 right-4 sm:top-24 sm:right-6 md:top-28 md:right-10 lg:right-16"
          data-testid="hero-dad-portrait"
          style={{
            background: HERO_TEXT,
            padding: "7px 7px 26px 7px",
            boxShadow:
              "0 12px 28px rgba(0,0,0,0.55), 0 2px 6px rgba(0,0,0,0.35)",
            transform: "rotate(-2.5deg)",
            transformOrigin: "top right",
            animationDelay: "0.6s",
          }}
        >
          <img
            src={SAILOR.portrait}
            alt={SAILOR.portraitCaption}
            loading="eager"
            className="block w-[88px] h-[110px] sm:w-[112px] sm:h-[140px] md:w-[140px] md:h-[174px] lg:w-[160px] lg:h-[200px] object-cover"
            style={{ filter: "grayscale(0.2) sepia(0.1) contrast(1.05)" }}
          />
          <figcaption
            className="font-mono-plex mt-2 text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-center"
            style={{ color: "#222" }}
          >
            T. P. Field · 1962
          </figcaption>
        </figure>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16 w-full">
        <div className="max-w-3xl">
          <div
            className="hero-fade flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8"
            style={{ animationDelay: "0.05s" }}
          >
            <span
              className="font-mono-plex uppercase tracking-[0.22em] text-[11px] sm:text-xs"
              style={{ color: HERO_BRASS }}
              data-testid="hero-overline"
            >
              A Life · 1946 – Present
            </span>
            <span className="flex-1 h-px" style={{ background: `${HERO_BRASS}4D` }} />
          </div>

          <h1
            className="hero-fade font-serif-grand font-semibold text-[2.5rem] xs:text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight"
            style={{ color: HERO_TEXT, animationDelay: "0.18s" }}
            data-testid="hero-name"
          >
            For Dad —
            <br />
            <span className="italic" style={{ color: HERO_BRASS }}>
              Terence
            </span>{" "}
            Peter Field
          </h1>

          <p
            className="hero-fade mt-6 sm:mt-8 md:mt-10 font-serif-display text-base sm:text-xl md:text-2xl italic max-w-2xl leading-relaxed"
            style={{ color: `${HERO_TEXT}CC`, animationDelay: "0.34s" }}
            data-testid="hero-tagline"
          >
            “From a sixteen-year-old boy entrant at HMS Ganges, to Chief Petty
            Officer Mechanician on Polaris patrol, to a global energy-industry
            consultant — and the places you and Mum saw together in between.
            A life, gathered in one place.”
          </p>

          {/* Chapter index — three lines of contents */}
          <ol
            className="hero-fade mt-8 sm:mt-10 space-y-1.5 sm:space-y-2 max-w-2xl"
            style={{ animationDelay: "0.5s" }}
            data-testid="hero-chapter-index"
          >
            <ChapterLink
              numeral="I"
              title="The Navy"
              dates="1962 — 1978"
              hash="#chapter-i"
            />
            <ChapterLink
              numeral="II"
              title="A Career in Information"
              dates="1978 — 2014"
              hash="#chapter-ii"
            />
            <ChapterLink
              numeral="III"
              title="The World, Mapped"
              dates="Travels, Business & Leisure"
              hash="#chapter-iii"
            />
          </ol>
        </div>
      </div>

      <a
        href="#chapter-i"
        className="hidden sm:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-10 transition flex-col items-center gap-2"
        style={{ color: HERO_BRASS }}
        onMouseEnter={(e) => (e.currentTarget.style.color = HERO_BRASS_BRIGHT)}
        onMouseLeave={(e) => (e.currentTarget.style.color = HERO_BRASS)}
        data-testid="hero-scroll-cue"
      >
        <span className="font-mono-plex uppercase tracking-[0.22em] text-xs">
          Begin
        </span>
        <ChevronDown size={20} className="animate-bounce" />
      </a>
    </section>
  );
}

function ChapterLink({ numeral, title, dates, hash }) {
  return (
    <li>
      <a
        href={hash}
        className="group flex items-baseline gap-3 sm:gap-4 py-1.5 sm:py-2 transition-colors"
        style={{ color: HERO_TEXT }}
        data-testid={`hero-chapter-${numeral.toLowerCase()}-link`}
        onMouseEnter={(e) => (e.currentTarget.style.color = HERO_BRASS_BRIGHT)}
        onMouseLeave={(e) => (e.currentTarget.style.color = HERO_TEXT)}
      >
        <span
          className="font-serif-display italic text-lg sm:text-xl md:text-2xl w-7 sm:w-8 shrink-0"
          style={{ color: HERO_BRASS }}
        >
          {numeral}.
        </span>
        <span className="font-serif-display text-base sm:text-lg md:text-xl leading-tight">
          {title}
        </span>
        <span className="flex-1 border-b border-dotted self-end mb-1.5" style={{ borderColor: `${HERO_BRASS}55` }} />
        <span className="font-mono-plex text-[10px] sm:text-xs uppercase tracking-[0.18em] shrink-0" style={{ color: `${HERO_TEXT}99` }}>
          {dates}
        </span>
      </a>
    </li>
  );
}
